import { useMemo, useRef, useState, useCallback, useContext } from 'react'

import cn from 'classnames'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import config from '@app/config'
import { ChatContext } from '@app/context'
import {
  getDateFnsLocale,
  getSystemMessageTranslation,
  isMobileDevice,
} from '@app/helpers'
import { useConversationLanguage, useUser } from '@app/hooks'
import type { MessageItem as MessageItemType, Source } from '@app/models'
import { getLocalDate, isB2BView } from '@app/utils'

import { Loader } from '@lib/components'

import { FollowUpActions } from './FollowUpActions'
import { AttachmentListingsProvider } from './MessageAttachments/MessageListingAttachments'
import { MessageItem } from './MessageItem'
import { SpamStatusMessage } from './SpamStatusMessage'
import { SystemMessageItem } from './SystemMessageItem/SystemMessageItem'
import { TranslationDrawer } from './TranslationDrawer'
import styles from './styles.module.scss'
import { DialogMessageEntry, DialogProps } from './types'
import { useFollowUpEntry } from './useFollowUpEntry'

const SUPPORTED_SOURCES: Source[] = ['Chat', 'Email', 'Lead']

export const Dialog = ({
  conversation,
  loading,
  isStaff,
  isSpamConversation,
  onOpenContactDetails,
  onPrefillReply,
  activeFollowUpAction,
  onSelectFollowUpAction,
}: DialogProps) => {
  const { t, i18n } = useTranslation()
  const locale = getDateFnsLocale(i18n.language)
  const { state } = useContext(ChatContext)
  const { currentUser } = useUser()
  const { isTranslationEnabled, languageName } =
    useConversationLanguage(conversation)
  const [abortController, setAbortController] =
    useState<AbortController | null>(null)
  const [isModalOpen, setModalOpen] = useState(false)

  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  const isB2B = isB2BView()
  const regionalLanguage = config.regionalSettings?.language

  const systemMessages = useMemo<MessageItemType[]>(
    () =>
      conversation.messages
        .filter((message) => message.source === 'System')
        .filter((message) =>
          getSystemMessageTranslation(
            message,
            conversation,
            t,
            state,
            currentUser?.id,
            isB2B,
            regionalLanguage,
            isStaff
          )
        ),
    [
      conversation,
      currentUser?.id,
      isB2B,
      regionalLanguage,
      state,
      t,
      isStaff,      
    ]
  )

  const followUpEntry = useFollowUpEntry({
    conversation,
  })

  const messagesByDate = useMemo(() => {
    const formatDate = (date: Date): string => {
      return format(date, 'd MMM yyyy', {
        locale,
      })
    }

    const groupedMessagesByDate = new Map<string, DialogMessageEntry[]>()

    const conversationEntries: DialogMessageEntry[] = conversation.messages
      .filter((message) => SUPPORTED_SOURCES.includes(message.source))
      .map((message) => ({
        kind: 'conversation',
        message,
        creationDate: message.creationDate,
      }))

    const systemMessageEntries: DialogMessageEntry[] = systemMessages.map(
      (message) => ({
        kind: 'system',
        message,
        creationDate: message.creationDate,
      })
    )

    const baseEntries: DialogMessageEntry[] = [
      ...conversationEntries,
      ...systemMessageEntries,
    ]
    if (followUpEntry) {
      baseEntries.push(followUpEntry)
    }

    baseEntries
      .sort((a, b) => {
        const aDate = new Date(a.creationDate)
        const bDate = new Date(b.creationDate)

        return bDate.getTime() - aDate.getTime()
      })
      .forEach((entry) => {
        const creationDate = getLocalDate(new Date(entry.creationDate))
        const formattedDate = formatDate(creationDate)

        if (!groupedMessagesByDate.has(formattedDate)) {
          groupedMessagesByDate.set(formattedDate, [])
        }

        groupedMessagesByDate.get(formattedDate)?.push(entry)
      })

    return groupedMessagesByDate
  }, [conversation.messages, followUpEntry, locale, systemMessages])

  const getAbortController = (): AbortController => {
    if (abortController) {
      abortController.abort()
    }
    const newController = new AbortController()
    setAbortController(newController)
    return newController
  }

  const handleMessageHover = useCallback(
    (messageId: string) => {
      if (openMenuId && openMenuId !== messageId) {
        setOpenMenuId(null)
      }
    },
    [openMenuId]
  )

  const longPressTimeout = useRef<NodeJS.Timeout | null>(null)

  const handleLongPressStart = useCallback((messageId: string) => {
    longPressTimeout.current = setTimeout(() => {
      if (isMobileDevice()) {
        setOpenMenuId(messageId)
      }
    }, 500)
  }, [])

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimeout.current) {
      clearTimeout(longPressTimeout.current)
      longPressTimeout.current = null
    }
  }, [])

  const hoverHandler = useCallback(
    (messageId: string) => () => handleMessageHover(messageId),
    [handleMessageHover]
  )
  const touchStartHandler = useCallback(
    (messageId: string) => () => {
      handleLongPressStart(messageId)
    },
    [handleLongPressStart]
  )

  const modalOpenHandler = useCallback(() => {
    setModalOpen(true)
  }, [])

  const modalCloseHandler = useCallback(() => {
    setModalOpen(false)
  }, [])

  const messagesClassName = useMemo(
    () => cn(styles.messages, styles.list, { [styles.noTouchCallout]: isStaff }),
    [isStaff]
  )

  return (
    <AttachmentListingsProvider conversation={conversation}>
      <div className={messagesClassName}>
        {isSpamConversation && (
          <SpamStatusMessage isStaff={isStaff} />
        )}
        {[...messagesByDate.entries()].map(([date, messages], index, array) => (
          <div key={date}>
            <div
              className={cn(
                styles.dialogsdate,
                index === array.length - 1 && languageName && styles.last
              )}
            >
              {date}
            </div>
            <div className={styles.list}>
              {messages.map((entry) => {
                if (entry.kind === 'followUpActions') {
                  return (
                    <FollowUpActions
                      key={`follow-up-actions-${entry.creationDate}`}
                      conversation={conversation}
                      onCallContact={onOpenContactDetails}
                      onPrefillReply={onPrefillReply}
                      activeAction={activeFollowUpAction}
                      onSelectAction={onSelectFollowUpAction}
                    />
                  )
                }
                if (entry.kind === 'system') {
                  return (
                    <SystemMessageItem
                      key={`system-${entry.message.id}-${entry.creationDate}`}
                      message={entry.message}
                      conversation={conversation}
                    />
                  )
                }

                const message = entry.message

                return (
                  <div
                    key={message.uuid || message.id}
                    onMouseEnter={hoverHandler(message.id.toString())}
                    onTouchStart={touchStartHandler(message.id.toString())}
                    onTouchEnd={handleLongPressEnd}
                    onTouchMove={handleLongPressEnd}
                  >
                    <MessageItem
                      message={message}
                      conversation={conversation}
                      abortController={getAbortController}
                      openMenuId={openMenuId}
                      setOpenMenuId={setOpenMenuId}
                      isStaff={isStaff}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        ))}
        {languageName && (
          <div className={styles.translationCaption} onClick={modalOpenHandler}>
            {isTranslationEnabled &&
              t('Chat.TranslationCaption.TranslationIsOn', { languageName })}
            {!isTranslationEnabled &&
              t('Chat.TranslationCaption.TranslationIsOff', { languageName })}
            &nbsp;
            <u>
              {isTranslationEnabled && t('Chat.TranslationCaption.TurnItOff')}
              {!isTranslationEnabled && t('Chat.TranslationCaption.TurnItOn')}
            </u>
          </div>
        )}
        {loading && <Loader />}
      </div>
      {languageName && isModalOpen && (
        <TranslationDrawer
          onClose={modalCloseHandler}
          translationEnabled={isTranslationEnabled}
          language={languageName}
          conversation={conversation}
        />
      )}
    </AttachmentListingsProvider>
  )
}

